import { useCallback, useState } from 'react';
import {
  ChartData,
  MAX_CANDLE_COUNT,
  StockInfo,
} from '../../../shared/types/game.types';
import { fetchRandomDailyStockData } from '../services/stockDataService';
import { convertServerDataToChartData } from '../utils/dataConverter';

interface UseStockDataProps {
  accessToken: string | null;
  apiBaseUrl: string;
  refreshAccessToken: () => Promise<string | null>;
}

/**
 * 주식 데이터 관리 훅
 */
export const useStockData = ({
  accessToken,
  apiBaseUrl,
  refreshAccessToken,
}: UseStockDataProps) => {
  const [cachedData, setCachedData] = useState<ChartData[]>([]);
  const [currentDataIndex, setCurrentDataIndex] = useState(0);
  const [stockInfo, setStockInfo] = useState<StockInfo | null>(null);

  const fetchStockData = useCallback(async () => {
    if (!accessToken) {
      throw new Error('인증 토큰이 필요합니다. 로그인이 필요합니다.');
    }

    const result = await fetchRandomDailyStockData({
      accessToken,
      apiBaseUrl,
      refreshAccessToken,
    });

    return result;
  }, [accessToken, apiBaseUrl, refreshAccessToken]);

  const convertToChartData = useCallback(
    (serverData: any[], stockCode: string, stockName: string) => {
      const chartData = convertServerDataToChartData(serverData);

      if (chartData.length === 0) {
        throw new Error('유효한 차트 데이터가 없습니다.');
      }

      // 주식 정보 설정
      const firstItem = serverData[0];
      const lastItem = serverData[serverData.length - 1];

      // 게임 시작을 위한 데이터 분할
      // MAX_CANDLE_COUNT만큼의 봉을 게임 데이터로 사용
      const gameStartIndex = Math.max(0, chartData.length - MAX_CANDLE_COUNT);
      const backgroundData = chartData.slice(0, gameStartIndex);
      const gameData = chartData.slice(gameStartIndex);

      // 게임 시작 날짜와 종료 날짜: 서버에서 받은 전체 데이터의 시작일과 종료일
      // 이 날짜들은 게임 시작 시점에 받은 랜덤 데이터의 전체 기간을 나타냄
      const gameStartDate = firstItem.tradingDate || firstItem.trading_date;
      const gameEndDate = lastItem.tradingDate || lastItem.trading_date;

      // 종목명 사용 (서버에서 반환한 값을 그대로 사용)
      // 서버에서 이미 종목명이 없거나 종목코드와 같으면 종목코드를 반환하므로
      // 클라이언트에서는 서버가 반환한 값을 그대로 사용
      const finalStockName = stockName || stockCode;

      setStockInfo({
        stockCode,
        stockName: finalStockName,
        startDate: gameStartDate, // 전체 데이터의 시작 날짜
        endDate: gameEndDate, // 전체 데이터의 종료 날짜
        gameStartDate, // 게임 시작 시점에 받은 데이터의 시작일 (저장용)
        gameEndDate, // 게임 시작 시점에 받은 데이터의 종료일 (저장용)
      });

      console.log('🎯 StockInfo 설정:', {
        stockCode,
        stockName,
        finalStockName,
        stockNameIsCode: stockName === stockCode,
      });

      console.log('🎯 게임 데이터 설정:', {
        backgroundDataLength: backgroundData.length,
        gameDataLength: gameData.length,
        firstCandlePrice: gameData[0]?.close,
        firstCandle: gameData[0],
        lastCandle: gameData[gameData.length - 1],
        gameStartDate, // 서버에서 받은 전체 데이터의 시작일
        gameEndDate, // 서버에서 받은 전체 데이터의 종료일
        serverDataFirstDate: firstItem.tradingDate || firstItem.trading_date,
        serverDataLastDate: lastItem.tradingDate || lastItem.trading_date,
      });

      if (gameData[0]) {
        console.log('🔍 실제 사용될 첫 번째 캔들 데이터:', {
          open: gameData[0].open,
          high: gameData[0].high,
          low: gameData[0].low,
          close: gameData[0].close,
          timestamp: gameData[0].timestamp,
          time: gameData[0].time,
        });
      }

      if (gameData.length === 0) {
        throw new Error('게임 데이터가 없습니다.');
      }

      setCachedData(gameData);
      setCurrentDataIndex(0);

      return {
        backgroundData,
        gameData,
      };
    },
    []
  );

  const getNextCachedData = useCallback((): ChartData | null => {
    const nextIndex = currentDataIndex + 1;

    if (nextIndex >= cachedData.length) {
      return null;
    }

    const nextData = cachedData[nextIndex];
    setCurrentDataIndex(nextIndex);
    return { ...nextData };
  }, [cachedData, currentDataIndex]);

  const fetchAdditionalData = useCallback(async () => {
    try {
      console.log('📡 추가 데이터 요청 중...');
      const { serverData } = await fetchStockData();

      if (
        !serverData ||
        !Array.isArray(serverData) ||
        serverData.length === 0
      ) {
        console.warn('⚠️ 추가 데이터가 없습니다.');
        return null;
      }

      const additionalData = convertServerDataToChartData(serverData);

      console.log('✅ 추가 데이터 변환 완료:', additionalData.length, '개');

      if (additionalData.length === 0) {
        console.warn('⚠️ 유효한 추가 데이터가 없습니다.');
        return null;
      }

      // 캐시에 추가
      setCachedData(prev => [...prev, ...additionalData]);

      return additionalData;
    } catch (error) {
      console.error('❌ 추가 데이터를 가져오는데 실패했습니다:', error);
      return null;
    }
  }, [fetchStockData]);

  const resetStockData = useCallback(() => {
    setCachedData([]);
    setCurrentDataIndex(0);
    setStockInfo(null);
  }, []);

  return {
    cachedData,
    currentDataIndex,
    stockInfo,
    fetchStockData,
    convertToChartData,
    getNextCachedData,
    fetchAdditionalData,
    resetStockData,
    setStockInfo,
  };
};
